/**
 * Tabs logic (design/ux/01_foundations.md §3.14): one Tab stop, ← / → move and
 * activate (automatic activation), Début / Fin first / last, disabled tabs
 * skipped; the active tab is addressable as `?tab=<id>` (AUD-NAV-19).
 */
import { nextRadioIndex } from "@/lib/radio-group.js";

/** URL parameter that carries the active tab. */
export const TAB_PARAM = "tab";

const TAB_KEYS = Object.freeze({
  horizontal: Object.freeze(["ArrowLeft", "ArrowRight", "Home", "End"]),
  // A vertical list (the Paramètres section list, WP-20): ↑ / ↓ move, ← / → are left to the page.
  vertical: Object.freeze(["ArrowUp", "ArrowDown", "Home", "End"]),
});

/**
 * Next tab index for a key, or null when the key is not a tab-list key
 * (horizontal strip: ↑ / ↓ are left to the page; vertical list: ← / →).
 * @param {boolean[]} enabled
 * @param {number} current
 * @param {string} key
 * @param {"horizontal"|"vertical"} [orientation="horizontal"]
 */
export function nextTabIndex(enabled, current, key, orientation = "horizontal") {
  if (!(TAB_KEYS[orientation] ?? TAB_KEYS.horizontal).includes(key)) return null;
  return nextRadioIndex(enabled, current, key);
}

/**
 * The tab to show for a `?tab=` value: the requested one when it exists and is
 * enabled, else the fallback (a stale or foreign deep link never blanks the
 * page).
 * @param {string|null|undefined} requested
 * @param {Array<{value: string, disabled?: boolean}>} items
 * @param {string} fallback
 */
export function resolveTab(requested, items, fallback) {
  const hit = items.find((t) => t.value === requested && !t.disabled);
  if (hit) return hit.value;
  const fb = items.find((t) => t.value === fallback && !t.disabled);
  return fb ? fb.value : (items.find((t) => !t.disabled)?.value ?? fallback);
}
